// src/components/ProcurementPanel.tsx — v2 (IDC-01, Nov 3, 2026): print/mail orders (order_kind 'mail') show pieces, route
//   and mail-by date instead of delivery; wording says "the supplier" instead of Mutual.
// PROC-01 M6: one supplier order, its quote and every check,
// with Approve (owner) / Request changes / Reject. All decisions go through approve-procurement-order;
// the console never writes procurement tables itself.
import { useEffect, useState } from "react";
import { canApply, ops, type ProcCheck, type ProcOrder } from "../lib/ops";
import { dateTime } from "../lib/format";
import { useOperator, useToast } from "../lib/context";
import { Loading, Notice } from "./ui";
import { Panel } from "./Panel";

const usd = (n: number | null | undefined) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const day = (d: string | null | undefined) => (d ? new Date(d + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }) : "—");

function holdLeft(iso: string | null | undefined): string {
  if (!iso) return "no quote date";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return `expired ${dateTime(iso)}`;
  const h = Math.floor(ms / 3_600_000);
  return h >= 48 ? `${Math.floor(h / 24)} days left` : `${h} h left`;
}

/** A suggested message for the supplier from the blocking checks (the owner edits it before sending). */
function suggestion(checks: ProcCheck[]): string {
  const asks: string[] = [];
  for (const c of checks) {
    if (c.ok || c.severity === "info") continue;
    if (c.id === "tax") asks.push("Please remove the sales tax: this purchase is for resale under our WA reseller permit.");
    if (c.id === "hold") asks.push("The inventory hold on this quote has expired. Please re-issue it with a new hold.");
    if (c.id === "po_match") asks.push("Please put our PO number in the Purchase Order field.");
    if (c.id === "items") asks.push(c.detail);
    if (c.id === "account") asks.push("Please put this quote on our account.");
  }
  return asks.join("\n");
}

export function ProcurementPanel(props: { orderId: string; title: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const [o, setO] = useState<ProcOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [mode, setMode] = useState<"none" | "changes" | "reject">("none");
  const [note, setNote] = useState("");

  useEffect(() => {
    let live = true;
    ops.procurementOrder(props.orderId).then((d) => live && setO(d)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [props.orderId]);

  const q = o?.quote ?? null;
  const checks = (q?.checks?.results ?? []) as ProcCheck[];
  const blocking = checks.filter((c) => !c.ok && c.severity === "block");
  const flags = checks.filter((c) => c.severity === "flag");
  const isOwner = role === "owner";
  const mayAct = canApply(role);

  async function run(action: "approve" | "reject" | "request_changes") {
    setBusy(action); setError(null);
    try {
      const r = await ops.procurementAct(props.orderId, action, note.trim() || undefined, action === "approve" ? ack : undefined);
      toast(action === "approve" ? `✓ Approved — sent to ${r.sent_to}${r.test_mode ? " (TEST)" : ""}`
        : action === "request_changes" ? `✓ Sent to ${r.sent_to}${r.test_mode ? " (TEST)" : ""}` : "✓ Rejected");
      await props.onDone();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function openPdf() {
    try { const r = await ops.procurementAct(props.orderId, "pdf_url"); if (r.url) window.open(r.url, "_blank", "noopener"); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <Panel kicker="Supplier order" title={props.title} onClose={props.onClose}>
      {!o && !error && <Loading what="the order" />}
      {error && <Notice kind="error">{error}</Notice>}
      {o && (
        <>
          {o.test_mode && <Notice kind="info">TEST MODE — every email from this order goes only to the test address, never to {o.supplier}.</Notice>}
          <dl className="facts">
            <dt>PO</dt><dd className="mono">{o.po_number}</dd>
            <dt>Supplier</dt><dd>{o.supplier} · {o.location?.name}</dd>
            <dt>Quote</dt><dd>{q ? <>{q.supplier_doc_no ?? "—"} · received {dateTime(q.received_at)} {q.has_pdf && <button type="button" className="btn quiet" onClick={openPdf}>Open PDF</button>}</> : "No quote yet"}</dd>
            {o.order_kind === "mail" && o.mail && (<>
              <dt>Print job</dt><dd>{o.mail.pieces} pieces · {o.mail.route ? `EDDM ZIP ${o.mail.zip} route ${o.mail.route}` : `addressed, ZIP ${o.mail.zip ?? "—"}`}{o.mail.campaign_id ? ` · ${o.mail.campaign_id}` : ""}</dd>
              <dt>Mail by</dt><dd>{day(o.mail.mail_by)}</dd>
            </>)}
            {o.order_kind !== "mail" && <dt>{o.fulfillment === "will_call" ? "Will call" : "Delivery"}</dt>}
            {o.order_kind !== "mail" && <dd>{o.fulfillment === "will_call"
              ? <>Pickup {day(o.pickup_on)}{o.pickup_window ? ` · ${o.pickup_window}` : ""} · {o.pickup_contact ?? "—"}</>
              : <>{String(o.ship_to?.address ?? "")} · {o.delivery_latest && o.delivery_latest !== o.deliver_on ? `between ${day(o.deliver_on)} and ${day(o.delivery_latest)}` : `on ${day(o.deliver_on)}`}{o.delivery_window ? ` · ${o.delivery_window}` : ""}</>}</dd>}
            {o.confirmed_delivery_on && (<><dt>Confirmed</dt><dd>{day(o.confirmed_delivery_on)}{o.confirmed_delivery_window ? ` · ${o.confirmed_delivery_window}` : ""}</dd></>)}
            {o.fulfillment !== "will_call" && o.delivery_instructions && (<><dt>{o.order_kind === "mail" ? "Notes" : "Instructions"}</dt><dd>{o.delivery_instructions}</dd></>)}
            <dt>Hold</dt><dd>{holdLeft(q?.hold_expires_at)}</dd>
            <dt>Tax</dt><dd>{o.tax_exempt_today ? "Resale (exempt)" : "Taxable"}{q ? ` · quote charges ${usd(q.totals?.tax)}` : ""}</dd>
          </dl>

          {q && (
            <div className="table-scroll">
              <table className="proc-lines">
                <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Unit</th><th className="num">Net</th></tr></thead>
                <tbody>
                  {q.lines.map((l, i) => (
                    <tr key={i} className={l.kind !== "product" ? "charge" : ""}>
                      <td><span className="mono">{l.sku}</span><br /><span className="muted">{l.description}</span></td>
                      <td className="num">{l.qty} {l.unit?.toLowerCase()}</td>
                      <td className="num mono">{l.unit_price}</td>
                      <td className="num mono">{usd(l.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={3}>Subtotal</td><td className="num mono">{usd(q.totals?.subtotal)}</td></tr>
                  <tr><td colSpan={3}>Charges</td><td className="num mono">{usd(q.totals?.charges)}</td></tr>
                  <tr><td colSpan={3}>Tax</td><td className="num mono">{usd(q.totals?.tax)}</td></tr>
                  <tr className="total"><td colSpan={3}>Total</td><td className="num mono">{usd(q.totals?.total)}</td></tr>
                </tfoot>
              </table>
            </div>
          )}

          {checks.length > 0 && (
            <ul className="proc-checks">
              {[...blocking, ...flags, ...checks.filter((c) => c.severity === "info")].map((c) => (
                <li key={c.id} className={!c.ok && c.severity === "block" ? "block" : c.severity === "flag" ? "flag" : "pass"}>
                  <span className="proc-mark" aria-hidden>{!c.ok && c.severity === "block" ? "✕" : c.severity === "flag" ? "!" : "✓"}</span>
                  <span>{c.detail}</span>
                </li>
              ))}
            </ul>
          )}

          {!mayAct && <Notice kind="info">Viewers can see supplier orders but not act on them.</Notice>}
          {mayAct && q && mode === "none" && (
            <div className="proc-actions">
              {isOwner && (
                <>
                  {o.status === "needs_review" && blocking.length === 0 && (
                    <label className="proc-ack"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> I've read the flags above</label>
                  )}
                  <button type="button" className="btn primary wide" disabled={!!busy || blocking.length > 0 || (o.status === "needs_review" && !ack)} onClick={() => run("approve")}>
                    {busy === "approve" ? "Sending approval…" : blocking.length ? `Approve (blocked by ${blocking.length})` : "Approve and send"}
                  </button>
                </>
              )}
              <button type="button" className="btn wide" disabled={!!busy} onClick={() => { setMode("changes"); setNote(suggestion(checks)); }}>Request changes…</button>
              <button type="button" className="btn quiet" disabled={!!busy} onClick={() => { setMode("reject"); setNote(""); }}>Reject…</button>
            </div>
          )}
          {mayAct && mode !== "none" && (
            <>
              <textarea className="reason" rows={4} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder={mode === "changes" ? "What should the supplier change? This text is emailed to them in the quote's thread." : "Why reject? (not emailed)"} aria-label="Note" />
              <div className="proc-actions">
                <button type="button" className="btn primary wide" disabled={!!busy || note.trim().length < 5} onClick={() => run(mode === "changes" ? "request_changes" : "reject")}>
                  {busy ? "Working…" : mode === "changes" ? "Send to supplier" : "Reject order"}
                </button>
                <button type="button" className="btn quiet" disabled={!!busy} onClick={() => setMode("none")}>Cancel</button>
              </div>
            </>
          )}

          {o.history.length > 0 && (
            <details className="proc-history"><summary>Email history ({o.history.length})</summary>
              <ol>{o.history.map((h, i) => <li key={i}>{dateTime(h.at)} · {h.direction === "out" ? "→" : "←"} {h.doc_type} · {h.status} · {h.subject}</li>)}</ol>
            </details>
          )}
        </>
      )}
    </Panel>
  );
}
